import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { countryOptions, countryName, flagEmoji } from '../../utils/countries';

interface Props {
  value: string;
  onChange: (v: string) => void;
  /** Optional id so a <label htmlFor> can point at the control. */
  id?: string;
}

/** Searchable dropdown over the ISO 3166 country list, with localized names +
 *  flags. Mirrors CurrencySelect. Used for nationality / passport pickers. */
export function CountrySelect({ value, onChange, id }: Props) {
  const { t, i18n } = useTranslation();
  const [open, setOpen]     = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [query, setQuery]   = useState('');
  const ref                 = useRef<HTMLDivElement>(null);
  const btnRef              = useRef<HTMLButtonElement>(null);

  const options = useMemo(() => countryOptions(i18n.language), [i18n.language]);

  function toggle() {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropUp(window.innerHeight - rect.bottom < 300);
    }
    setOpen(o => !o);
    setQuery('');
  }

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.trim()
    ? options.filter(c =>
        c.code.toLowerCase().includes(query.toLowerCase()) ||
        c.name.toLowerCase().includes(query.toLowerCase())
      )
    : options;

  return (
    <div ref={ref} className="relative">
      <button
        ref={btnRef}
        id={id}
        type="button"
        onClick={toggle}
        className="input w-full flex items-center gap-2 text-left"
      >
        <span className="text-base leading-none">{value ? flagEmoji(value) : '🌍'}</span>
        <span className="text-sm truncate">{value ? countryName(value, i18n.language) : t('common.select')}</span>
        <svg className="w-3.5 h-3.5 text-slate-400 shrink-0 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className={`absolute z-50 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden ${dropUp ? 'bottom-full mb-1' : 'mt-1'}`}>
          <div className="p-2 border-b border-slate-100 dark:border-slate-700">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t('common.search')}
              className="w-full px-2.5 py-1.5 text-sm text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:border-teal-400 transition-colors placeholder:text-slate-400"
            />
          </div>
          <ul className="overflow-y-auto" style={{ maxHeight: 220 }}>
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-400 italic">{t('common.noResults')}</li>
            )}
            {filtered.map(c => (
              <li key={c.code}>
                <button
                  type="button"
                  onClick={() => { onChange(c.code); setOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer ${
                    c.code === value ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300' : 'text-slate-700 dark:text-slate-300'
                  }`}
                >
                  <span className="text-base leading-none shrink-0">{c.flag}</span>
                  <span className="truncate">{c.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
